
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { addDays, format, parseISO, eachDayOfInterval } from "date-fns";
import { DateRange } from "react-day-picker";
import { supabase } from "@/integrations/supabase/client";

export const AnalyticsTab = () => {
  const [date, setDate] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });
  const [ordersData, setOrdersData] = useState<any[]>([]);
  const [recommendationsData, setRecommendationsData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!date?.from || !date?.to) return;

      const fromDate = format(date.from, "yyyy-MM-dd");
      const toDate = format(date.to, "yyyy-MM-dd");

      // Fetch orders data
      const { data: orders } = await supabase
        .from('orders')
        .select('created_at')
        .gte('created_at', fromDate)
        .lte('created_at', toDate)
        .order('created_at');

      // Fetch recommendations data
      const { data: recommendations } = await supabase
        .from('coach_recommendations')
        .select('created_at')
        .gte('created_at', fromDate)
        .lte('created_at', toDate)
        .order('created_at');

      // Generate array of all days in the date range
      const allDays = eachDayOfInterval({
        start: date.from,
        end: date.to
      });

      // Initialize data for all days with zero values
      const initialData = allDays.reduce((acc: any, day) => {
        const dateStr = format(day, 'MMM dd');
        acc[dateStr] = 0;
        return acc;
      }, {});

      // Process orders data
      const ordersByDate = orders?.reduce((acc: any, order) => {
        const dateStr = format(parseISO(order.created_at), 'MMM dd');
        acc[dateStr] = (acc[dateStr] || 0) + 1;
        return acc;
      }, {...initialData}) || initialData;

      // Process recommendations data
      const recommendationsByDate = recommendations?.reduce((acc: any, rec) => {
        const dateStr = format(parseISO(rec.created_at), 'MMM dd');
        acc[dateStr] = (acc[dateStr] || 0) + 1;
        return acc;
      }, {...initialData}) || initialData;

      // Convert to array format for charts, maintaining date order
      const ordersArray = allDays.map(day => ({
        name: format(day, 'MMM dd'),
        value: ordersByDate[format(day, 'MMM dd')] || 0,
      }));

      const recommendationsArray = allDays.map(day => ({
        name: format(day, 'MMM dd'),
        value: recommendationsByDate[format(day, 'MMM dd')] || 0,
      }));

      setOrdersData(ordersArray);
      setRecommendationsData(recommendationsArray);
    };

    fetchData();
  }, [date]);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <DatePickerWithRange date={date} setDate={setDate} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ordersData}>
                <defs>
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9b87f5" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#9b87f5" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#9b87f5"
                  fillOpacity={1}
                  fill="url(#colorOrders)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daily Coach Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={recommendationsData}>
                <defs>
                  <linearGradient id="colorRecommendations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f587b8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f587b8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#f587b8"
                  fillOpacity={1}
                  fill="url(#colorRecommendations)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
